import { Card, Grid, Image } from '@design-systems/canvas-ui'
import React, { useMemo } from 'react'
import { Table } from 'components/table-compat/table-compat'
import { useUltaLoyalty } from 'hooks/brands/useBrandSales'
import { useEssentials } from 'hooks/utils/useEssentials'
import { format } from 'date-fns'
import { buildDataTableObject } from 'utils/data'
import { BasicPlaceholder } from 'components/placeholder'
import { transformUltaLoyaltyData } from './helpers'
import { TRANSLATION_KEYS } from 'locales/translation-keys'

interface UltaBrandLoyaltyProps {
    timePeriodIndex: number
}

export const UltaBrandLoyalty: React.FC<UltaBrandLoyaltyProps> = ({
    timePeriodIndex,
}) => {
    const { env, session, currentLocation, pathname, t } = useEssentials()
    const { loyalty, brands } = TRANSLATION_KEYS
    const timePeriods = [
        'today',
        'yesterday',
        'week_to_date',
        'month_to_date',
        'year_to_date',
    ]
    const selectedTimePeriod = timePeriods[timePeriodIndex]

    const ultaLoyaltyFilters = useMemo(() => {
        const baseFilters = {
            date: format(new Date(), 'yyyy-MM-dd'),
            time_dimension: selectedTimePeriod,
        }
        return baseFilters
    }, [selectedTimePeriod])

    const { data, isLoading } = useUltaLoyalty({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        filters: ultaLoyaltyFilters,
    })

    const tableData = useMemo(() => {
        return transformUltaLoyaltyData(data, currentLocation, pathname, t)
    }, [data, currentLocation, pathname, t])

    const tableObjectData = useMemo(() => {
        if (!data || tableData.rows.length === 0) return null

        return buildDataTableObject({
            currentData: tableData,
            widths: [120, 200],
            pinFirstColumn: true,
            sortable: false,
            isLoading,
        })
    }, [tableData, data, isLoading])

    return (
        <Card className="hc8-pa-3x">
            <Grid.Container align="center" justify="space-between">
                <Grid.Item className="hc8-pa-6x">{t(loyalty.title)}</Grid.Item>
                <Grid.Item>
                    <Image
                        src="/img/UltaRewards.svg"
                        alt="Ulta Rewards"
                        width="100px"
                        height="60px"
                    />
                </Grid.Item>
            </Grid.Container>
            <Grid.Container>
                <Grid.Item
                    className="hc8-pl-none hc8-pr-none hc8-pt-3x hc8-ta-center"
                    size={{
                        xs: 12,
                    }}
                >
                    {timePeriodIndex === 0 && <div>{t(brands.noData)}</div>}
                    {isLoading && timePeriodIndex !== 0 && (
                        <div
                            className="placeholder-container"
                            data-testid="placeholder"
                        >
                            <BasicPlaceholder type="text" rows={1} />
                            <BasicPlaceholder
                                type="table"
                                rows={4}
                                columns={4}
                            />
                        </div>
                    )}
                    {!isLoading && tableObjectData && (
                        <Table data={tableObjectData} scrollableWidth={true} />
                    )}
                </Grid.Item>
            </Grid.Container>
        </Card>
    )
}

export default UltaBrandLoyalty
